<template>
  <div>
    <v-dialog
      v-if="showAddShareDialog"
      :value="true"
      persistent
      max-width="500px"
    >
      <v-card>
        <v-card-title>Add Share</v-card-title>
        <v-card-text>
          <v-card flat>
            <v-form
              v-model="isValid"
              class="form-share"
            >
              <v-text-field
                v-model="name"
                :rules="rules.text"
                label="Shareholder"
                :counter="MAX_LENGTH"
                required
                autofocus
              />
              <v-text-field
                v-model="shareEmail"
                :rules="rules.email"
                :counter="MAX_LENGTH"
                label="Email"
                type="email"
                required
              />
              <v-text-field
                v-model="numberOfShares"
                :rules="rules.number"
                :counter="MAX_LENGTH"
                label="No. Issued Shares"
                type="number"
                required
              />
              <v-text-field
                v-model="discount"
                :rules="rules.currency"
                label="Discount"
                placeholder="100"
                suffix="cents"
                min="0"
                hint="Discount in cents"
              />
              <v-select
                v-model="shareType"
                :items="shareTypeList"
                :rules="rules.selectOne(shareTypeList)"
                :disabled="isReadOnly"
                label="Type"
                required
              />
              <v-select
                v-if="isShareTypeOrdinary()"
                v-model="shareTypeClass"
                :items="shareTypeClassList"
                :rules="rules.selectOne(shareTypeClassList)"
                label="Share Class"
                required
              />
              <v-textarea
                v-if="showConditions()"
                v-model="conditions"
                label="Specific conditions or preferences attached to these shares"
                rows="2"
              />
              <v-select
                v-model="series"
                :items="seriesListSelections"
                :rules="rules.selectOne(seriesListSelections)"
                label="Series"
                required
              />
            </v-form>
            <v-card-actions>
              <v-btn
                color="primary"
                text
                large
                @click.stop="onCancelClick"
              >
                Cancel
              </v-btn>
              <v-btn
                :disabled="!isValid"
                :loading="loading"
                color="primary"
                large
                @click.stop="onSubmitData"
              >
                {{ inEditMode ? 'Save Shareholder' : 'Add Shareholder' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-btn
      color="primary"
      @click.stop="showAddShareDialog = true"
    >
      New Shareholder
    </v-btn>
  </div>
</template>

<script>

const SHARE_TYPES = ['Ordinary', 'Preference', 'Partly Paid', 'Non-voting']
const SHARE_TYPE_CLASS = ['Class A', 'Class B']
const MAX_LENGTH = 40

export default {
  name: 'UpdateShareHolding',
  props: {
    seriesList: { type: Array, default: undefined },
    textActionBtn: { type: String, default: 'Add Shareholder' },
    editHolding: { type: Object, default: undefined }
  },
  data: function () {
    return {
      isValid: false,
      name: null,
      shareEmail: null,
      numberOfShares: null,
      shareType: null,
      shareTypeClass: '',
      discount: 0,
      series: null,
      shareTypeList: SHARE_TYPES,
      shareTypeClassList: SHARE_TYPE_CLASS,
      isReadOnly: false,
      MAX_LENGTH,
      loading: false,
      showAddShareDialog: false,
      conditions: undefined,
      rules: {
        text: [value => this.rulesAction(value, 'text')],
        number: [value => this.rulesAction(value, 'number')],
        email: [value => this.rulesAction(value, 'email')],
        selectOne: (list) => [value => this.rulesAction(value, 'selectOne', list)]
      }
    }
  },
  computed: {
    seriesListSelections() {
      let seriesListSelections = this.seriesList
      // Only filter out closed series when not in edit mode
      if (!this.inEditMode) {
        seriesListSelections = seriesListSelections.filter(item => item.open)
      }
      return seriesListSelections.map(item => item.name)
    },
    inEditMode() {
      return !!this.editHolding
    }

  },
  watch: {
    editHolding: {
      handler: function (newEditHolding) {
        if (newEditHolding) {
          this.showAddShareDialog = true
          this.setLocalValues(newEditHolding)
        }
      },
      immediate: true // important so it triggers on first creation of the component
    }
  },
  beforeUpdate: function () {
    if (!this.inEditMode) {
      this.series = this.seriesListSelections[0] || null
    }
  },
  methods: {
    isShareTypeOrdinary() {
      if (this.shareType === 'Ordinary') {
        return true
      }

      this.shareTypeClass = ''
      return false
    },
    showConditions() {
      // Only Ordinary Class A should hide conditions but hide when nothing is selected
      const result = (this.shareType && this.shareType !== SHARE_TYPES[0]) || (this.shareTypeClass && this.shareTypeClass !== SHARE_TYPE_CLASS[0])
      if (!result) {
        this.conditions = ''
      }
      return result
    },
    onCancelAction() {
      this.loading = false
      this.showAddShareDialog = false
    },
    onSubmitData() {
      const data = {
        email: this.shareEmail,
        series: this.series,
        numberOfShares: Number(this.numberOfShares),
        shareType: this.shareType,
        discountInCents: parseInt(this.discount || 0),
        name: this.name,
        shareTypeClass: this.shareTypeClass,
        dateIssued: new Date().toString(),
        conditions: this.conditions || ''
      }
      this.loading = true
      const finallyUnload = () => {
        this.loading = false
        this.$emit('onSubmitData')
      }
      if (this.editHolding) {
        const holdingUpdate = {
          id: this.editHolding.id,
          ...data
        }
        this.$handlingError('Failed to update shareholder', () => this.$api.putShareHolding(holdingUpdate).then(() => {
          this.$showSuccessbar('Share updated!')
          this.showAddShareDialog = false
          this.setLocalValues({})
        }), { final: finallyUnload })
      } else {
        this.$handlingError('Failed to add shareholder', () => this.$api.addShareHolding(data).then(() => {
          this.$showSuccessbar('Share added!')
          this.showAddShareDialog = false
          this.setLocalValues({})
        }), { final: finallyUnload })
      }
    },
    onRemove() {
      this.setLocalValues({})
      this.$emit('onSubmitData')
    },
    onCancelClick() {
      this.setLocalValues({})
      this.showAddShareDialog = false
      this.$emit('onCancelClick', 'shares')
    },
    setLocalValues(values) {
      if (values) {
        this.name = values.name || null
        this.shareEmail = values.email || null
        this.numberOfShares = values.numberOfShares || null
        this.shareType = values.shareType || null
        this.shareTypeClass = values.shareTypeClass || ''
        this.series = values.series || null
        this.discount = values.discountInCents || 0
        this.conditions = values.conditions || ''
      }
    },
    rulesAction(value, type, options) {
      if (!value) {
        return 'This field is required.'
      }

      switch (type) {
      case 'email':
        // TODO It is isn't reasonable to validate email by regex so this needs to be changed
        const pattern = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/
        return pattern.test(value) || 'E-mail must be valid'
      case 'text':
        return value.length <= MAX_LENGTH || `Max ${MAX_LENGTH} characters`
      case 'number':
        return typeof parseInt(value, 10) === 'number' || 'Just numbers are accepted'
      case 'currency':
        return (Number.isInteger(parseFloat(value)) && parseInt(value) > 0) || 'Just whole numbers greater than 0 are accepted'
      case 'selectOne':
        return options.includes(value) || 'Select a valid option from the list'
      }
    }
  }
}
</script>
